/**
 * Parse Judgment Selling SSOT markdown → in-app help view-model.
 * Document is SOT; UI must not hardcode sell copy from this surface.
 *
 * Markers: `<!-- help:section id="…" -->` (runs until next marker / EOF).
 */

export const JUDGMENT_SELL_TRAIT_IDS = ["instinct", "insight", "bond"] as const;
export type JudgmentSellTraitId = (typeof JUDGMENT_SELL_TRAIT_IDS)[number];

export type JudgmentSellPitch = {
  id: JudgmentSellTraitId;
  nameKo: string;
  pitchKo: string;
  causalKo: string;
};

export const JUDGMENT_HELP_SECTION_IDS = [
  "contract",
  "raid_leader",
  "forbidden",
  "soft_ship",
  "trait.instinct",
  "trait.insight",
  "trait.bond",
  "collective",
  "trait.grit",
  "polar",
] as const;

export type JudgmentHelpSectionId = (typeof JUDGMENT_HELP_SECTION_IDS)[number];

export type JudgmentTraitHelpId = JudgmentSellTraitId | "grit";

export type JudgmentTraitHelp = {
  id: JudgmentTraitHelpId;
  nameKo: string;
  /** Short contract / tag line (after em dash in §0). */
  tagKo: string;
  /** Sell fantasy (`**셀링**` row). */
  pitchKo: string;
  /** Raise-axis causality (`**인과**` row). */
  causalKo: string;
  gainHigh: string;
  gainLow: string;
  failHigh: string;
  failLow: string;
  /** Full section markdown (mermaid stripped for panels). */
  bodyMd: string;
};

/** Raid Leader panel SOT — short trait cores only (`raid_leader` section). */
export type RaidLeaderHelp = {
  /** One short player-facing intro line. */
  introPlain: string;
  cores: Readonly<Record<JudgmentTraitHelpId, string>>;
};

export type JudgmentSellHelpDoc = {
  topic: string;
  title: string;
  sections: Readonly<Record<JudgmentHelpSectionId, string>>;
  traits: Readonly<Record<JudgmentTraitHelpId, JudgmentTraitHelp>>;
  sellSheet: readonly JudgmentSellPitch[];
  /** Planner/Sim Lab intro (contract blockquotes + Soft≪AA note). */
  introPlain: string;
  /** Player-facing Raid Leader cores (no Soft≪AA / Claim B walls). */
  raidLeader: RaidLeaderHelp;
};

const RAID_LEADER_TRAIT_NAMES: Readonly<
  Record<JudgmentTraitHelpId, string>
> = {
  instinct: "직감",
  insight: "통찰",
  bond: "유대",
  grit: "인내",
};

const SECTION_RE =
  /<!--\s*help:section\s+id="([a-z0-9_.]+)"\s*-->/gi;

const TRAIT_HEADING_RE =
  /^##\s+[\d.]+\s+재능\s+상세\s+[—\-]\s+(.+?)\s+\(`(\w+)`\)/m;

const GRIT_HEADING_RE = /^##\s+[\d.]+\s+~~(.+?)~~\s+`(\w+)`/m;

function stripFrontmatter(md: string): { topic: string; body: string } {
  const m = md.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!m) return { topic: "", body: md };
  const fm = m[1] ?? "";
  const topic = (fm.match(/^topic:\s*(.+)$/m)?.[1] ?? "").trim();
  return { topic, body: m[2] ?? "" };
}

function stripMermaid(md: string): string {
  return md.replace(/```mermaid\r?\n[\s\S]*?```/gi, "").trim();
}

function cellText(raw: string): string {
  return raw
    .replace(/\*\*/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function tableRowValue(md: string, label: string): string {
  const re = new RegExp(
    `^\\|\\s*\\*\\*${label}\\*\\*\\s*\\|\\s*(.+?)\\s*\\|\\s*$`,
    "im",
  );
  const m = md.match(re);
  return m ? cellText(m[1] ?? "") : "";
}

function parseTradeoff(md: string): {
  gainHigh: string;
  gainLow: string;
  failHigh: string;
  failLow: string;
} {
  const gain = md.match(
    /^\|\s*이득\s*\|\s*(.+?)\s*\|\s*(.+?)\s*\|\s*$/im,
  );
  const fail = md.match(
    /^\|\s*실패\s*\|\s*(.+?)\s*\|\s*(.+?)\s*\|\s*$/im,
  );
  return {
    gainHigh: gain ? cellText(gain[1] ?? "") : "",
    gainLow: gain ? cellText(gain[2] ?? "") : "",
    failHigh: fail ? cellText(fail[1] ?? "") : "",
    failLow: fail ? cellText(fail[2] ?? "") : "",
  };
}

/** Map Korean sell name → trait id from contract blockquotes. */
function parseContractTags(contractMd: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of contractMd.split(/\r?\n/)) {
    const m = line.match(/>\s*\*\*(.+?)\*\*\s*[—\-]\s*(.+?)\s*\.?\s*$/);
    if (!m) continue;
    out[cellText(m[1] ?? "")] = cellText(m[2] ?? "");
  }
  return out;
}

function parseTraitSection(
  id: JudgmentTraitHelpId,
  bodyMd: string,
  contractTags: Record<string, string>,
): JudgmentTraitHelp {
  const cleaned = stripMermaid(bodyMd);
  let nameKo = "";
  if (id === "grit") {
    const gh = cleaned.match(GRIT_HEADING_RE);
    nameKo = gh ? cellText(gh[1] ?? "인내") : "인내";
    const pitchKo = tableRowValue(cleaned, "셀링");
    const causalKo = tableRowValue(cleaned, "인과");
    if (!pitchKo || !causalKo) {
      throw new Error(
        "judgment-sell-help: trait.grit missing **셀링** / **인과** rows",
      );
    }
    const trade = parseTradeoff(cleaned);
    if (!trade.gainHigh || !trade.gainLow || !trade.failHigh || !trade.failLow) {
      throw new Error(
        "judgment-sell-help: trait.grit missing 0 vs 100 tradeoff (이득/실패)",
      );
    }
    return {
      id,
      nameKo,
      tagKo: contractTags[nameKo] ?? "역할 바닥 (판매 제외)",
      pitchKo,
      causalKo,
      ...trade,
      bodyMd: cleaned,
    };
  }

  const th = cleaned.match(TRAIT_HEADING_RE);
  if (!th) {
    throw new Error(
      `judgment-sell-help: trait.${id} missing heading "재능 상세 — Name (\`${id}\`)"`,
    );
  }
  const headingId = th[2] ?? "";
  if (headingId !== id) {
    throw new Error(
      `judgment-sell-help: trait section id=${id} but heading id=${headingId}`,
    );
  }
  nameKo = cellText(th[1] ?? "");
  const pitchKo = tableRowValue(cleaned, "셀링");
  const causalKo = tableRowValue(cleaned, "인과");
  if (!pitchKo || !causalKo) {
    throw new Error(
      `judgment-sell-help: trait.${id} missing **셀링** / **인과** rows`,
    );
  }
  const trade = parseTradeoff(cleaned);
  if (!trade.gainHigh || !trade.gainLow || !trade.failHigh || !trade.failLow) {
    throw new Error(
      `judgment-sell-help: trait.${id} missing 0 vs 100 tradeoff (이득/실패)`,
    );
  }
  return {
    id,
    nameKo,
    tagKo: contractTags[nameKo] ?? pitchKo,
    pitchKo,
    causalKo,
    ...trade,
    bodyMd: cleaned,
  };
}

function splitSections(
  body: string,
): Map<string, string> {
  const hits: { id: string; index: number; markerEnd: number }[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(SECTION_RE.source, "gi");
  while ((m = re.exec(body)) !== null) {
    hits.push({
      id: (m[1] ?? "").toLowerCase(),
      index: m.index,
      markerEnd: m.index + m[0].length,
    });
  }
  const out = new Map<string, string>();
  for (let i = 0; i < hits.length; i++) {
    const cur = hits[i]!;
    const end = i + 1 < hits.length ? hits[i + 1]!.index : body.length;
    out.set(cur.id, body.slice(cur.markerEnd, end).trim());
  }
  return out;
}

function requireSections(
  found: Map<string, string>,
): Record<JudgmentHelpSectionId, string> {
  const missing = JUDGMENT_HELP_SECTION_IDS.filter((id) => !found.has(id));
  if (missing.length > 0) {
    throw new Error(
      `judgment-sell-help: missing help:section id(s): ${missing.join(", ")}`,
    );
  }
  const sections = {} as Record<JudgmentHelpSectionId, string>;
  for (const id of JUDGMENT_HELP_SECTION_IDS) {
    sections[id] = found.get(id) ?? "";
  }
  return sections;
}

function introFromContract(contractMd: string, softShipMd: string): string {
  const quotes = contractMd
    .split(/\r?\n/)
    .filter((l) => l.trim().startsWith(">"))
    .map((l) => cellText(l.replace(/^>\s*/, "")))
    .filter(Boolean);
  const softLine = softShipMd
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith("#") && !l.startsWith("|"));
  const parts = [...quotes];
  if (softLine) parts.push(cellText(softLine));
  parts.push("극단 ≠ 항상 유리 (Soft≪AA polar · Claim B 금지).");
  return parts.join(" ");
}

function parseRaidLeaderSection(md: string): RaidLeaderHelp {
  const cleaned = stripMermaid(md);
  const nameToId = new Map<string, JudgmentTraitHelpId>(
    (
      Object.entries(RAID_LEADER_TRAIT_NAMES) as [
        JudgmentTraitHelpId,
        string,
      ][]
    ).map(([id, name]) => [name, id]),
  );
  const cores = {} as Record<JudgmentTraitHelpId, string>;
  for (const line of cleaned.split(/\r?\n/)) {
    const m = line.match(/>\s*\*\*(.+?)\*\*\s*[—\-]\s*(.+?)\s*\.?\s*$/);
    if (!m) continue;
    const id = nameToId.get(cellText(m[1] ?? ""));
    if (!id) continue;
    cores[id] = cellText(m[2] ?? "");
  }
  const missing = (
    Object.keys(RAID_LEADER_TRAIT_NAMES) as JudgmentTraitHelpId[]
  ).filter((id) => !cores[id]);
  if (missing.length > 0) {
    throw new Error(
      `judgment-sell-help: raid_leader missing core(s): ${missing.join(", ")}`,
    );
  }
  const introPlain =
    cleaned
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find(
        (l) =>
          l.length > 0 &&
          !l.startsWith("#") &&
          !l.startsWith(">") &&
          !l.startsWith("|") &&
          !l.startsWith("```") &&
          !l.startsWith("<!--"),
      ) ?? "직감·통찰·유대로 판단 특색을 고릅니다. 인내는 역할 바닥.";
  return { introPlain: cellText(introPlain), cores };
}

/**
 * Pure parse. Throws if required `<!-- help:section id="…" -->` markers
 * or trait sell/tradeoff tables are missing.
 */
export function parseJudgmentSellHelp(md: string): JudgmentSellHelpDoc {
  if (!md || !md.trim()) {
    throw new Error("judgment-sell-help: empty markdown");
  }
  const { topic, body } = stripFrontmatter(md);
  const titleMatch = body.match(/^#\s+(.+)$/m);
  const title = cellText(titleMatch?.[1] ?? "Judgment Selling");
  const found = splitSections(body);
  const sections = requireSections(found);
  const contractTags = parseContractTags(sections.contract);
  const traits = {
    instinct: parseTraitSection(
      "instinct",
      sections["trait.instinct"],
      contractTags,
    ),
    insight: parseTraitSection(
      "insight",
      sections["trait.insight"],
      contractTags,
    ),
    bond: parseTraitSection("bond", sections["trait.bond"], contractTags),
    grit: parseTraitSection("grit", sections["trait.grit"], contractTags),
  } as const;

  const sellSheet: JudgmentSellPitch[] = (
    ["instinct", "insight", "bond"] as const
  ).map((id) => ({
    id,
    nameKo: traits[id].nameKo,
    pitchKo: traits[id].pitchKo,
    causalKo: traits[id].causalKo,
  }));

  return {
    topic,
    title,
    sections,
    traits,
    sellSheet,
    introPlain: introFromContract(sections.contract, sections.soft_ship),
    raidLeader: parseRaidLeaderSection(sections.raid_leader),
  };
}

/** Hi/lo blurbs for help rows — gain + fail (polar tradeoff). */
export function traitHelpHiLo(
  trait: JudgmentTraitHelp,
  pole: "high" | "low",
): string {
  if (pole === "high") {
    return `높음: ${trait.gainHigh} · 실패 ${trait.failHigh}`;
  }
  return `낮음: ${trait.gainLow} · 실패 ${trait.failLow}`;
}

/** Raid Leader core line for a trait (SSOT `raid_leader`). */
export function raidLeaderCore(
  doc: JudgmentSellHelpDoc,
  id: JudgmentTraitHelpId,
): string {
  return doc.raidLeader.cores[id];
}
